<template>
  <el-select
    ref="control"
    class="ui-select"
    :class="{ 'is-block': block }"
    :value="value"
    :popper-class="resolvedPopperClass"
    v-bind="$attrs"
    v-on="forwardedListeners"
    @input="$emit('input', $event)"
    @visible-change="handleVisibleChange"
  >
    <slot />
  </el-select>
</template>

<script>
export default {
  name: 'UiSelect',
  inheritAttrs: false,
  props: {
    value: { type: [String, Number, Array], default: '' },
    popperClass: { type: String, default: '' },
    block: { type: Boolean, default: true },
    matchDropdownWidth: { type: Boolean, default: true }
  },
  computed: {
    resolvedPopperClass () {
      return ['terminal-dropdown-menu', 'ui-select-dropdown', this.popperClass].filter(Boolean).join(' ')
    },
    forwardedListeners () {
      const listeners = { ...this.$listeners }
      delete listeners.input
      delete listeners['visible-change']
      return listeners
    }
  },
  methods: {
    handleVisibleChange (visible) {
      if (visible && this.matchDropdownWidth) this.syncDropdownWidth()
      if (!visible && this.shouldReleaseAndroidFocus()) {
        // Element UI 会在单选完成后重新聚焦只读 input。部分 Android WebView
        // 不会随后清除 is-focus，导致选框一直保留激活边框。
        this.$nextTick(() => {
          const control = this.$refs.control
          const activeElement = document.activeElement
          if (control?.$el?.contains(activeElement)) control.blur()
        })
      }
      this.$emit('visible-change', visible)
    },
    shouldReleaseAndroidFocus () {
      const isEnabledAttr = value => value === '' || value === true || value === 'true'
      if (isEnabledAttr(this.$attrs.filterable) || isEnabledAttr(this.$attrs.multiple)) return false
      if (typeof navigator === 'undefined') return false
      return /Android/i.test(navigator.userAgent) || document.documentElement.classList.contains('is-cordova-android')
    },
    syncDropdownWidth () {
      this.$nextTick(() => {
        const control = this.$refs.control
        const dropdown = control?.$refs?.popper
        const popper = dropdown?.$el
        if (!control?.$el || !popper) return
        const width = `${control.$el.getBoundingClientRect().width}px`
        popper.style.setProperty('width', width, 'important')
        popper.style.setProperty('min-width', width, 'important')
        // Reposition after shrinking long options; the initial placement used their natural width.
        dropdown.updatePopper()
      })
    },
    focus () {
      if (this.$refs.control) this.$refs.control.focus()
    },
    blur () {
      if (this.$refs.control) this.$refs.control.blur()
    }
  }
}
</script>

<style lang="scss">
.ui-select {
  height: var(--ui-control-height);
  box-sizing: border-box;
  line-height: 1;
  vertical-align: top;
}

.ui-select.is-block {
  width: 100%;
}

.ui-select > .el-input {
  height: 100%;
  box-sizing: border-box;
  line-height: 1;
  vertical-align: top;
}

.ui-select .el-input__inner {
  height: var(--ui-control-height);
  box-sizing: border-box;
  color: var(--ui-text-primary);
  border-color: var(--ui-control-rest-border);
  border-radius: var(--ui-radius-medium);
  background: linear-gradient(var(--ui-control-rest-background), var(--ui-control-rest-background)), var(--ui-bg-control);
  font-size: var(--ui-font-size-form-control);
  font-weight: 400;
  line-height: var(--ui-control-height);
}

.ui-select .el-input__suffix,
.ui-select .el-input__suffix-inner,
.ui-select .el-input__icon {
  height: var(--ui-control-height);
  line-height: var(--ui-control-height);
}

.ui-select .el-input--small .el-input__inner {
  height: var(--ui-control-height);
  line-height: var(--ui-control-height);
}

.ui-select .el-input--mini .el-input__inner {
  height: var(--ui-control-height);
  line-height: var(--ui-control-height);
}

.ui-select .el-input__inner::placeholder {
  color: var(--ui-control-rest-placeholder);
}

.ui-select .el-select__caret {
  color: var(--ui-expand-caret-color);
  font-size: 14px;
  transform: none !important;
  transition: color var(--ui-duration-fast) ease,
    transform var(--ui-duration-fast) ease;
}

.ui-select .el-select__caret.el-icon-arrow-up::before {
  content: "\e790";
}

.ui-select .el-select__caret.is-reverse {
  transform: rotate(180deg) !important;
}

.ui-select-dropdown .el-select-dropdown__list {
  padding: var(--ui-space-xs) 0 !important;
}

.ui-select-dropdown .el-select-dropdown__item {
  height: 36px;
  line-height: 36px;
}
</style>
